import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import { generateSubscriptionPreview } from 'services/api';
import { parseDataToApi } from 'utils/parseDataToApi';

import {
  ActionPortal,
  DefaultActionButtons,
  Icon,
  Step,
  Form,
  LoadingSpin,
  Button,
  Text,
  FormLabel,
  Input,
  Checkbox,
} from 'components';

import {
  Gray,
  Green,
  Orange,
  Purple,
  White,
} from 'constants/colors';
import { StepPropTypes } from 'constants/propTypes';

const formatToCurrency = (value) => {
  const hasValue = !!value;
  if (!hasValue) return "R$ 0,00"
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' });
}

const TestInput = ({
  title,
  name,
  relative,
  popoAmount,
  popoPrices,
  popoDiscount,
  popoUnitPrice,
}) => {
  const baseAmount = popoAmount[name]
  const amount = relative ? relative.reduce((acc, item) => acc + (popoAmount[item] || 0), 0) : baseAmount
  const total_price = relative && !popoPrices[name] ? relative.reduce((acc, item) => acc + popoDiscount[item] || 0, 0) : popoPrices[name]
  const discounted_price = popoDiscount[name]
  const unit_price = popoUnitPrice[name]

  const has_discount = popoPrices[name] && !!discounted_price ? discounted_price < total_price : false

  if (!baseAmount || baseAmount === 0) return null;
  if (!amount || amount === 0) return null;
  return (
    <div style={{
      display: "grid",
      gridColumn: "1",
      columnGap: "30px",
      gridTemplateColumns: "250px 150px 100px 100px",
      alignItems: 'center',
      borderBottom: `1px ${Gray[250]} solid`,
      padding: "20px 15px"
    }}>
      <Text
        style={{ whiteSpace: "wrap" }}
        color={Gray[500]}
        fontWeight={500}
        fontSize={"16px"}
      >
        {title}
      </Text>

      <Text
        style={{ whiteSpace: "wrap" }}
        color={Gray[300]}
        fontWeight={500}
        fontSize={"16px"}
      >
        {amount || 0}
      </Text>

      <Text
        style={{ whiteSpace: "wrap" }}
        color={Gray[300]}
        fontWeight={500}
        fontSize={"16px"}
      >
        {!!unit_price ? `R$: ${unit_price}` : ""}
      </Text>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <Text
          color={has_discount ? Gray[400] : Purple[500]}
          fontWeight={500}
          fontSize={"16px"}
          style={{ textDecoration: has_discount ? "line-through" : "none" }}
        >
          {formatToCurrency(total_price)}
        </Text>

        {
          has_discount &&
          <Text
            color={Green[400]}
            fontWeight={500}
            fontSize={"16px"}
          >
            {formatToCurrency(discounted_price)}
          </Text>
        }
      </div>
    </div>
  )
}

const values = [
  {
    title: "Contele Tracker",
    name: "contele_tracker",
    relative: [
      "tracker_2g",
      "tracker_4g",
      "plug_and_play",
    ],
    hideInTable: true, // ← Oculta na renderização da tabela
  },
  {
    title: "Contele System",
    name: "contele_system",
  },
  {
    title: "Rastreador Plug & Play",
    name: "plug_and_play",
  },
  {
    title: "Rastreador 2G",
    name: "tracker_2g",
  },
  {
    title: "Rastreador 4G",
    name: "tracker_4g",
  },
  {
    title: "Bloqueio 12V",
    name: "blocking_12_volts",
  },
  {
    title: "Bloqueio 24V",
    name: "blocking_24_volts",
  },
  {
    title: "Atualização automática do Módulo de Multas",
    name: "fine_auto_update",
    relative: [
      "tracker_2g",
      "tracker_4g",
      "plug_and_play",
      "contele_system",
      "vehicle_camera",
      "vehicle_camera_ia",
    ]
  },
  {
    title: "Contele Cam PLUS",
    name: "vehicle_camera",
  },
  {
    title: "Contele Cam MAX",
    name: "vehicle_camera_ia",
  },
  {
    title: "Setup Câmeras único",
    name: "camera_setup",
    hideInTable: true,
  },
]

export default function Confirmation(props) {
  const queryParams = useLocation().search;
  const defaultDiscountCoupon = new URLSearchParams(queryParams).get('discountCoupon') || "";
  const [subscriptionPreview, setSubscriptionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [failLoadingPreview, setFailLoadingPreview] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(defaultDiscountCoupon);
  const [couponIsValid, setCouponIsValid] = useState(false);
  const [observation, setObservation] = useState("");
  const [popo, setPopo] = useState(defaultDiscountCoupon)
  const [aware, setAware] = useState(false);

  const {
    theme,
    onSubmit,
    onCancel,
    initialValues: {
      basicInfo, solutionInfo
    },
  } = props;

  const {
    blocker,
    vehicleManager,
    quantityTypes,
    cameraTypes,
    finesManager,
    trackersBatch,
  } = solutionInfo;

  const [popoPrices, setPopoPrices] = useState({
    total: null,
    contele_system: null,
    contele_tracker: null,
    plug_and_play: null,
    tracker_2g: null,
    tracker_4g: null,
    blocking_12_volts: null,
    blocking_24_volts: null,
    fine_auto_update: null,
    vehicle_camera: null,
    vehicle_camera_ia: null,
    camera_setup: null,
  })


  const [popoUnitPrice, setPopoUnitPrice] = useState({
    contele_system: null,
    plug_and_play: null,
    tracker_2g: null,
    tracker_4g: null,
    blocking_12_volts: null,
    blocking_24_volts: null,
    fine_auto_update: null,
    vehicle_camera: null,
    vehicle_camera_ia: null,
    camera_setup: null,
  })

  const [popoAmount, setPopoAmount] = useState({
    total_discount_amount: null,
    contele_system: vehicleManager?.conteleSystemCount,
    contele_tracker: vehicleManager?.conteleTrackerCount,
    plug_and_play: quantityTypes?.trackerPlugAndPlayCount,
    tracker_2g: quantityTypes?.tracker2GCount,
    tracker_4g: quantityTypes?.tracker4GCount,
    blocking_12_volts: blocker?.useBlocker && parseInt(blocker?.twelveVoltsCount),
    blocking_24_volts: blocker?.useBlocker && parseInt(blocker?.twentyFourVoltsCount),
    fine_auto_update: finesManager?.fineManager,
    vehicle_camera: cameraTypes?.conteleCamCount,
    vehicle_camera_ia: cameraTypes?.conteleCamIaCount,
    camera_setup: (cameraTypes?.conteleCamCount || 0) + (cameraTypes?.conteleCamIaCount || 0),
  })

  const [popoDiscount, setPopoDiscount] = useState({
    total: null,
    contele_system: null,
    contele_tracker: null,
    plug_and_play: null,
    tracker_2g: null,
    tracker_4g: null,
    blocking_12_volts: null,
    blocking_24_volts: null,
    fine_auto_update: null,
    vehicle_camera: null,
    vehicle_camera_ia: null,
    camera_setup: null,
  })

  const validateDiscountCoupon = async ({ discountCoupon, amount }) => {

    const isAllTrackers = trackersBatch?.isAllTrackers ?? true;

    const subscriptionPayload = {
      ...basicInfo,
      contele_tracker: amount.contele_tracker,
      contele_system: amount.contele_system,
      plug_and_play: amount.plug_and_play,
      tracker_2g: amount.tracker_2g,
      tracker_4g: amount.tracker_4g + amount.plug_and_play,
      blocking_12_volts: amount.blocking_12_volts,
      blocking_24_volts: amount.blocking_24_volts,
      fine_auto_update: amount.fine_auto_update,
      vehicle_camera: amount.vehicle_camera,
      vehicle_camera_ia: amount.vehicle_camera_ia,
      camera_setup: amount.camera_setup,
      discountCoupon,
      consultantId: basicInfo.gettingStarted?.seller_id,
      partial_shipment: !isAllTrackers,
      first_batch_quantity: isAllTrackers ? 0 : (trackersBatch?.trackersQuantity || 0),
    }

    const subscriptionData = parseDataToApi(subscriptionPayload);

    const responseSubscriptionPreview = await generateSubscriptionPreview({ subscriptionData });

    const {
      contele_subscription,
      billing_preview,
      message,
      errors
    } = responseSubscriptionPreview;

    const isInvalidDiscountCoupon = message && message === 'discount_code_not_available';
    const hasUnexpectedErrorOnCreatePreview = !!errors && !isInvalidDiscountCoupon;

    return {
      isValid: !isInvalidDiscountCoupon && !!billing_preview,
      billing_preview,
      contele_subscription,
      hasUnexpectedErrorOnCreatePreview
    }
  };

  const getSubscriptionPreview = async ({ discountCoupon, amount, failByCoupon }) => {
    setLoadingPreview(true);

    try {
      const couponResponse = await validateDiscountCoupon({ discountCoupon, amount });

      if (couponResponse?.hasUnexpectedErrorOnCreatePreview) {
        throw new Error('Unexpected error');
      };

      if (couponResponse?.contele_subscription?.invalid_codes?.length > 0) {
        setAppliedCoupon('');
        setCouponIsValid(false);
      }
      if (couponResponse?.contele_subscription?.billing_discounts?.length > 0) {
        setCouponIsValid(true);
      }

      if (couponResponse?.billing_preview) {
        setSubscriptionPreview(couponResponse?.billing_preview);

        const translate = {
          cgv_tracker_device_plug_and_play_unit_v0: "plug_and_play",
          cgv_tracker_fine_manager_unit_v0: "fine_auto_update",
          cgv_tracker_camera_unit_v0: "vehicle_camera",
          cgv_tracker_camera_ia_unit_v0: "vehicle_camera_ia",
          cgv_tracker_device_2G_unit_v0: "tracker_2g",
          cgv_tracker_device_4G_unit_v0: "tracker_4g",
          cgv_tracker_feature_fuel_lock_12_unit_v0: "blocking_12_volts",
          cgv_tracker_feature_fuel_lock_24_unit_v0: "blocking_24_volts",
          cgv_contele_system_unit_v0: "contele_system",
          cgv_cam_initial_setup_unit_v0: "camera_setup"
        }

        const formatted = couponResponse?.billing_preview.subscription_items.reduce((acc, item) => {
          acc.prices[translate[item.contele_product_id]] = item.total_price_without_discount_per_cycle
          acc.unit_prices[translate[item.contele_product_id]] = item.unit_price_per_cycle
          acc.amount[translate[item.contele_product_id]] = item.quantity
          acc.price_with_discount[translate[item.contele_product_id]] = item.total_price_with_discount_per_cycle
          return acc;
        }, {
          unit_prices: {},
          prices: {},
          amount: {},
          price_with_discount: {},
        })

        setPopoUnitPrice(state => ({
          ...state,
          ...formatted.unit_prices,
          plug_and_play: formatted.unit_prices.tracker_4g,
        }))

        setPopoPrices(state => ({
          ...state,
          ...formatted.prices,
          total: couponResponse?.billing_preview.total_price_without_discount_per_cycle,
          plug_and_play: formatted.unit_prices.tracker_4g * amount.plug_and_play,
          tracker_4g: formatted.prices.tracker_4g - (formatted.unit_prices.tracker_4g * amount.plug_and_play),
        }))
        setPopoAmount(state => ({
          ...state,
          ...formatted.amount,
          tracker_4g: (formatted.amount.tracker_4g || 0) - (amount.plug_and_play || 0),
          total_discount_amount: couponResponse?.billing_preview.total_discount_absolute_value_per_cycle
        }))
        setPopoDiscount(state => ({
          ...state,
          ...formatted.price_with_discount,
          total: couponResponse?.billing_preview.total_price_with_discount_per_cycle,
          plug_and_play: formatted.unit_prices.tracker_4g * amount.plug_and_play,
          tracker_4g: formatted.price_with_discount.tracker_4g - (formatted.unit_prices.tracker_4g * amount.plug_and_play),
        }))
      }

      setFailLoadingPreview(false);
    } catch (error) {
      setFailLoadingPreview(true);
    };

    setLoadingPreview(false);
  }

  useEffect(() => {
    getSubscriptionPreview({ discountCoupon: appliedCoupon, amount: popoAmount });
  }, []);

  const disabledCouponButton = popo?.length <= 3;

  const isSync = useMemo(() => {
    if (!subscriptionPreview?.subscription_items) return true;
    const excludeFromCount = ["total_discount_amount", "contele_tracker", "camera_setup"]

    const form = Object.entries(popoAmount).reduce((acc, [key, val]) => {
      if (excludeFromCount.includes(key)) return acc;
      if (key === "plug_and_play") return acc + (Number(val) || 0);
      if (key === "tracker_4g") {
        return acc + (Number(val) || 0) + (Number(popoAmount.plug_and_play) || 0);
      }
      return acc + (Number(val) || 0);
    }, 0)

    const previewData = Object.values(subscriptionPreview?.subscription_items)
      .filter(item => !item.contele_product_id?.includes('cgv_cam_initial_setup_unit'))
      .reduce((acc, val) => acc + val.quantity, 0)

    return form === previewData
  }, [popoAmount, subscriptionPreview])

  const submitForm = () => {
    const CONTELE_TRACKER_INDEX = 0
    const formValues = {
      prices: {
        ...popoPrices,
        contele_tracker: values[CONTELE_TRACKER_INDEX].relative.reduce((acc, item) => acc + (popoAmount[item] || 0), 0)
      },
      unit_price: popoUnitPrice,
      unit_amount: popoAmount,
      discounted_prices: {
        ...popoDiscount,
        contele_tracker: values[CONTELE_TRACKER_INDEX].relative.reduce((acc, item) => acc + (popoDiscount[item] || 0), 0)
      },
      coupon: appliedCoupon,
      observation,
    }
    onSubmit(formValues);
  };

  return (
    <Step
      headerTitle="Resumo da sua contratação"
      style={{
        height: '100%',
        width: "100%",
        padding: "24px",
        backgroundColor: Gray[200]
      }}
      headerStyle={{
        margin: 0,
      }}
    >
      {loadingPreview && !popoPrices.total &&
        <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpin
            show={loadingPreview}
            weight="5px"
            size="60px"
            backColor={Purple[500]}
            frontColor="transparent"
          />
        </div>
      }
      {!loadingPreview && !popoPrices.total && failLoadingPreview &&
        <div style={{ width: '100%', height: '200px', display: 'grid', alignItems: 'center', justifyItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <span>Tivemos um problema inesperado ao tentar gerar o valor de sua contratação.</span>
            <br />
            <span>Por favor, tente novamente dentro de alguns instantes, caso o erro persista, entre em contato com nosso financeiro.</span>
          </div>
          <Button
            width="200px"
            padding="10px"
            title="Tentar Novamente"
            onClick={() => getSubscriptionPreview({ amount: popoAmount })}
          />
        </div>
      }
      {!!popoPrices.total &&
        <Form
          onSubmit={(e) => {
            e.preventDefault()
          }}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'top',
            justifyContent: 'space-evenly'
          }}
        >
          <div
            style={{
              backgroundColor: White[100],
              width: "fit-content",
              borderRadius: "8px",
              border: `1px solid ${Gray[250]}`
            }}
          >
            <div style={{
              display: "grid",
              gridColumn: "1",
              columnGap: "30px",
              gridTemplateColumns: "250px 150px 100px 100px",
              alignItems: 'center',
              borderBottom: `1px ${Gray[250]} solid`,
              padding: "15px 10px",
            }}>
              <Text style={{ color: Gray[400] }}>{"Produto/Serviço"}</Text>
              <Text style={{ color: Gray[400] }}>{"Quantidade"}</Text>
              <Text style={{ color: Gray[400] }}>{"Valor Unitário"}</Text>
              <Text style={{ color: Gray[400] }}>{"Valor Total"}</Text>
            </div>
            {
              values
                .filter(item => !item.hideInTable) // ← Filtra itens ocultos
                .map((item, index) => (
                  <TestInput
                    key={`input-${item.title}-${index}`}
                    title={item.title}
                    popoAmount={popoAmount}
                    popoPrices={popoPrices}
                    popoDiscount={popoDiscount}
                    popoUnitPrice={popoUnitPrice}
                    name={item.name}
                    relative={item.relative}
                  />
                ))
            }

            <div
              style={{
                padding: "10px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <label htmlFor="observation">Observação: </label>
              <Input
                value={observation}
                onChange={(e) => {
                  setObservation(e.target.value)
                }}
                maxLength={500}
                type="textarea"
                placeholder="Digite aqui sua mensagem"
                style={{
                  height: '160px',
                  resize: "none",
                  border: `1px solid ${Gray[250]}`,
                  borderRadius: "5px",
                  marginTop: "10px",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "auto",
                  right: "15px",
                  bottom: "15px",
                  top: "auto"
                }}
              >
                {observation.length}/500
              </span>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: "30px",
              width: "35rem",
              height: "fit-content",
            }}
          >

            <div
              style={{
                backgroundColor: White[100],
                width: "100%",
                height: "fit-content",
                borderRadius: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  padding: "15px",
                  flexDirection: "column"
                }}
              >
                <FormLabel
                  for="discount_coupon"
                  style={{
                    width: "100%",
                  }}
                >Cupom</FormLabel>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    columnGap: "40px",
                    alignItems: "center",
                  }}
                >
                  <Input
                    type="text"
                    name="discount_coupon"
                    placeholder="Digite um cupom"
                    maxLength={256}
                    value={popo}
                    onChange={(e) => {
                      e.persist();
                      setPopo(e.target?.value)
                    }}
                    style={{
                      padding: "20px 12px",
                      fontSize: "14px",
                      width: "250px"
                    }}
                  />
                  <Button
                    title={"Aplicar cupom"}
                    onClick={() => {
                      setAppliedCoupon(popo)
                      getSubscriptionPreview({ discountCoupon: popo, amount: popoAmount })
                    }}
                    disabled={disabledCouponButton}
                    style={{
                      width: "150px",
                      padding: "22px",
                      fontSize: "16px",
                      fontWeight: "700",
                      borderRadius: "8px",
                      backgroundColor: disabledCouponButton ? Gray[200] : Purple[500],
                      border: 0,
                      gridArea: "malafaias"
                    }}
                    textConfig={{
                      style: {
                        color: disabledCouponButton ? Gray[300] : White[100],
                      }
                    }}
                  />
                </div>

                {couponIsValid &&
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      padding: "10px",
                      columnGap: "10px",
                      alignItems: "center",
                      justifyContent: "space-evenly",
                      alignContent: "center"
                    }}
                  >
                    <Icon
                      icon="solidCircleCheck"
                      width="18px"
                      height="18px"
                      color={Green[400]}
                    />
                    <p style={{
                      color: Green[400],
                      fontFamily: `"Roboto", sans-serif`,
                      margin: '0px',
                    }}>Cupom "{appliedCoupon}" aplicado</p>
                  </div>
                }
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  padding: "15px",
                  borderTop: `1px solid ${Gray[250]}`,
                  display: "flex",
                  justifyContent: "space-between"
                }}
              >
                {
                  !popoDiscount.total ?
                    <>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          width: "100%",
                          rowGap: "5px"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Text
                            style={{
                              color: Gray[500],
                            }}
                          >Valor total mensal: </Text>

                          <Text
                            style={{
                              color: Purple[500]
                            }}
                          >
                            {formatToCurrency(popoPrices.total)}
                          </Text>
                        </div>

                        {popoAmount.camera_setup > 0 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                          >
                            <Text
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                              }}
                            >
                              Setup Câmeras único:
                            </Text>

                            <Text
                              style={{
                                color: Purple[500],
                              }}
                            >
                              {formatToCurrency(popoPrices.camera_setup)}
                            </Text>
                          </div>
                        )}
                      </div>
                    </>
                    :
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        width: "100%",
                        rowGap: "5px"
                      }}
                    >
                      {couponIsValid && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          <Text
                            style={{
                              color: Gray[400],
                            }}
                          >
                            Valor mensal sem desconto
                          </Text>
                          <Text
                            style={{
                              color: Gray[400],
                              fontSize: "1em",
                            }}
                          >
                            {formatToCurrency(popoPrices.total)}
                          </Text>
                        </div>
                      )}

                      {couponIsValid && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <Text
                            style={{
                              color: Green[400],
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Desconto do cupom
                          </Text>
                          <Text
                            style={{
                              color: Green[400],
                            }}
                          >
                            - {formatToCurrency(popoAmount.total_discount_amount)}
                          </Text>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Text
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          Valor mensal:
                        </Text>

                        <Text
                          style={{
                            color: Purple[500],
                            fontSize: "16px",
                          }}
                        >
                          {formatToCurrency(popoDiscount.total)}
                        </Text>
                      </div>

                      {popoAmount.camera_setup > 0 && (
                        <>
                          <div
                            style={{
                              borderTop: `1px solid ${Gray[250]}`,
                              marginTop: "10px",
                              paddingTop: "10px"
                            }}
                          >
                            {popoPrices.camera_setup && popoDiscount.camera_setup && popoDiscount.camera_setup < popoPrices.camera_setup && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  marginBottom: "5px"
                                }}
                              >
                                <Text
                                  style={{
                                    color: Gray[400],
                                  }}
                                >
                                  Setup câmeras único sem desconto
                                </Text>

                                <Text
                                  style={{
                                    color: Gray[400],
                                    fontSize: "1em",
                                  }}
                                >
                                  {formatToCurrency(popoPrices.camera_setup)}
                                </Text>
                              </div>
                            )}

                            {popoPrices.camera_setup && popoDiscount.camera_setup && popoDiscount.camera_setup < popoPrices.camera_setup && (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "5px"
                                }}
                              >
                                <Text
                                  style={{
                                    color: Green[400],
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Desconto no setup
                                </Text>
                                <Text
                                  style={{
                                    color: Green[400],
                                  }}
                                >
                                  - {formatToCurrency(popoPrices.camera_setup - popoDiscount.camera_setup)}
                                </Text>
                              </div>
                            )}

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "14px",
                                fontWeight: "600",
                              }}
                            >
                              <Text
                                style={{
                                  color: Gray[500],
                                }}
                              >
                                Setup câmeras único
                              </Text>

                              <Text
                                style={{
                                  color: Purple[500],
                                  fontSize: "1em",
                                }}
                              >
                                {formatToCurrency(popoDiscount.camera_setup || popoPrices.camera_setup)}
                              </Text>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                }
              </div>
            </div>


            {couponIsValid && popoDiscount.total > 0 &&
              <div
                style={{
                  padding: '14px 16px',
                  display: 'flex',
                  width: '100%',
                  borderRadius: 8,
                  backgroundColor: Gray[100],
                  marginTop: 22,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Icon
                      color={Gray[400]}
                      icon="exclamationCircle"
                      style={{ width: '16px', height: '16px' }}
                    />
                    <Text
                      color={Gray[400]}
                      fontSize="16px"
                      lineHeight="19px"
                      fontWeight="700"
                    >
                      Alerta Importante: Condições do Desconto
                    </Text>
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      marginBottom: 20,
                      opacity: 0.8,
                      color: Gray[400],
                    }}
                  >
                    O desconto de {formatToCurrency(subscriptionPreview?.total_discount_absolute_value_per_cycle)} só será mantido nas faturas pagas até a data do vencimento. Caso a fatura seja paga após a data de vencimento, será cobrado o valor integral, sem o desconto.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      type="checkbox"
                      name="aware"
                      id="aware"
                      onChange={() => setAware(!aware)}
                      checked={aware}
                    />
                    <Text
                      color={Gray[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                      textAlign="center"
                    >
                      Sim, estou ciente.
                    </Text>
                  </div>
                </div>
              </div>
            }
            
            {couponIsValid && popoDiscount.total > 0 && !aware && (
              <div
                style={{
                  padding: '14px 16px',
                  display: 'flex',
                  width: '100%',
                  borderRadius: 8,
                  backgroundColor: Orange[100],
                  marginTop: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Icon
                      color={Orange[400]}
                      icon="exclamationTriangle"
                      style={{ width: '16px', height: '16px' }}
                    />
                    <Text
                      color={Orange[400]}
                      fontSize="16px"
                      lineHeight="19px"
                      fontWeight="700"
                    >
                      Requisitos para Prosseguir
                    </Text>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                  >
                    <Text
                      color={Orange[400]}
                      fontSize="14px"
                      lineHeight="19px"
                      fontWeight="400"
                    >
                      • É necessário aceitar as condições do desconto para prosseguir
                    </Text>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Form>
      }

      <ActionPortal>
        <DefaultActionButtons
          theme={theme}
          isValid={!loadingPreview && !failLoadingPreview && isSync && (!couponIsValid || aware)}
          backButtonStyle={{
            iconPosition: 'left',
            border: 'none',
            customBackgroundColor: 'transparent',
            customIconColor: Gray[400],
            customTextColor: Gray[400],
            title: 'Passo Anterior',
          }}
          nextButtonStyle={{
            customBackgroundColor: Purple[500],
            customIconColor: Gray[100],
            customTextColor: Gray[100],
            title: 'Próximo Passo',
          }}
          onCancel={onCancel}
          onSubmit={() => !loadingPreview && submitForm()}
        />
      </ActionPortal>
    </Step>
  );
}

Confirmation.propTypes = StepPropTypes;
